import type { EditorState } from '@tiptap/pm/state';

/** `}}` with only ordinary text before it — a `{` would be the start of a different token. */
const CLOSING = /^[^{}]*\}\}/;

/**
 * How far a token started at the suggestion's `from` actually runs.
 *
 * The suggestion range stops at the CARET, because the text between the trigger and the caret is
 * the query. That is the wrong thing to replace: with the caret inside an already-closed
 * `{{ investor.first_name }}`, inserting over the query alone leaves `tor.first_name }}` sitting
 * after the new field. So the closing braces are looked for ahead of the caret and included.
 *
 * Returns `to` unchanged when there is no `}}` ahead — a token still being typed has nothing
 * after the caret to consume.
 */
export const tokenReplacementEnd = (state: EditorState, to: number): number => {
	const $to = state.doc.resolve(to);
	const blockEnd = $to.end();
	if (blockEnd <= to) {
		return to;
	}
	const ahead = state.doc.textBetween(to, blockEnd, '\n', '￼');
	const closing = CLOSING.exec(ahead);
	return closing === null ? to : to + closing[0].length;
};

/**
 * True when a space should follow the inserted field.
 *
 * The space exists so the caret lands in text rather than against the atom, where the next
 * keystroke would be swallowed. Skipped when there is already whitespace there, or the field
 * ends the paragraph — two spaces in a deed read as a typo.
 */
export const needsTrailingSpace = (state: EditorState, at: number): boolean => {
	const $at = state.doc.resolve(at);
	const blockEnd = $at.end();
	if (blockEnd <= at) {
		return false;
	}
	const next = state.doc.textBetween(at, Math.min(at + 1, blockEnd), '\n', '￼');
	return next !== ' ' && next !== ' ';
};
